import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RotateCcw } from "lucide-react";
import { PanelSelect } from "../../../PanelSelect";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import type { RemovedBatchHeaderProps } from "./types";

export const RemovedBatchHeader = ({
  batch,
  circuitOptions,
  onRestore,
  onAssign,
}: RemovedBatchHeaderProps) => {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      circuitOptions.map((option) => ({
        value: option.circuitId,
        label: option.label,
        color: option.color,
      })),
    [circuitOptions],
  );

  const count = batch.devices.length;
  // On a one-device removal these repeat the device row's own two controls a
  // centimetre above them — the pair of identical run pickers he complained of.
  const hasBatchActions = count > 1;
  const canMove = Boolean(onAssign) && options.length > 0;

  return (
    <header className="px-1 pt-1.5">
      <div className="text-[13px] font-medium text-slate-500">
        {batch.ordinal > 0
          ? t("smartSurveyor.cablesBeta.removedGroupTitle", {
              index: batch.ordinal,
              count,
            })
          : t("smartSurveyor.cablesBeta.removedGroupEarlier", { count })}
      </div>

      {hasBatchActions && (
        <div className="mt-1.5 flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              trackEvent(
                ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
                { action: "restored", deviceCount: count },
              );
              onRestore();
            }}
            className="flex h-9 flex-none items-center gap-1.5 rounded-md border border-blue-200 bg-white px-2.5 text-[13px] font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-50"
          >
            <RotateCcw size={15} />
            {t("smartSurveyor.cablesBeta.removedGroupRestoreAll", { count })}
          </button>
          {canMove && (
            <span className="min-w-0 flex-1">
              <PanelSelect
                value=""
                options={options}
                placeholder={t(
                  "smartSurveyor.cablesBeta.removedGroupMoveAllOnto",
                  {
                    count,
                  },
                )}
                ariaLabel={t(
                  "smartSurveyor.cablesBeta.removedGroupMoveAllOnto",
                  {
                    count,
                  },
                )}
                onPick={(circuitId) => {
                  trackEvent(
                    ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
                    { action: "moved", deviceCount: count },
                  );
                  onAssign?.(circuitId);
                }}
              />
            </span>
          )}
        </div>
      )}
    </header>
  );
};
